// Mirrors src/shared/groupStage.ts's suggestGroupPlan (backend, pure
// function, no AWS imports) — REPLICATED here rather than imported, because
// the frontend is a fully separate TypeScript/Vite project: its own
// package.json ("ggm-portal"), its own tsconfig (`include: ["src"]`, rooted
// at frontend/src — nothing outside it type-checks), its own `tsc && vite
// build` pipeline, and no workspace/path-alias wiring back to the backend's
// src/shared. A relative `../../../src/shared/groupStage` import would sit
// outside tsconfig's `include` (silently un-type-checked) and outside Vite's
// project root, and there's no existing precedent for reaching across that
// boundary anywhere in frontend/src. Kept intentionally tiny + copied
// verbatim so it's trivial to eyeball against the source if that ever
// changes — see groupStage.ts's own doc comment on suggestGroupPlan.
export const MIN_GROUP_SIZE = 2;

export interface GroupPlan {
  numGroups: number;
  advancePerGroup: number;
}

/**
 * Suggest a sensible default split for `numTeams` aiming at an 8-team playoff:
 * 2 groups up to 12 teams, otherwise groups of ~6, advancing whatever makes the
 * playoff field a power of two (min 1 per group). Purely advisory — the caller
 * can override both numbers. 10 -> { numGroups: 2, advancePerGroup: 4 }.
 */
export function suggestGroupPlan(numTeams: number): GroupPlan {
  if (numTeams < MIN_GROUP_SIZE * 2) return { numGroups: 1, advancePerGroup: Math.max(1, numTeams - 1) };
  const numGroups = numTeams <= 12 ? 2 : Math.ceil(numTeams / 6);
  const smallestGroup = Math.floor(numTeams / numGroups);
  let playoff = 1;
  while (playoff * 2 <= numGroups * smallestGroup && playoff * 2 <= numTeams) playoff *= 2;
  const advancePerGroup = Math.max(1, Math.min(smallestGroup, Math.floor(playoff / numGroups) || 1));
  return { numGroups, advancePerGroup };
}
